/**
 * Save to Journal — button handler: validate name, capture grid, upload to Firebase Storage + Firestore.
 */

import { subscriptionManager } from './subscription.js';
import { captureGardenGridAsBlob, capturePlantSummaryAsBlob, captureGardenPlanAsBlob, captureGardenCalendarAsBlob } from './garden-layout-image.js';
import { assertGardenPlanLimitNotReached, assertGardenPlanNameIsUnique, savePlanSnapshotToJournal } from './journal-api.js';
import { loadExportLibraries } from './load-export-libs.js';
import { updateGardenCalendar } from '../scripts/calendar.js';

const JOURNAL_PLANT_INFO_TARGET_BYTES = 200 * 1024;
const JOURNAL_PLANT_INFO_MAX_DIMENSION = 1800;
const JOURNAL_PLANT_INFO_MIN_QUALITY = 0.45;
const JOURNAL_PLANT_INFO_SCALE_STEP = 0.85;

/**
 * Each occupied grid cell counts once toward its visible label (variety or base crop).
 * `plants` is unique display names in first-seen order for the dashboard journal list.
 * `plantCounts` is keyed by that same display string so per-variety counts match the grid.
 * `plantBaseByDisplay` maps variety label → base crop when they differ (Firestore / journal UI).
 */
function getPlantSummaryFromGrid() {
    const gridItems = document.querySelectorAll('.grid-item-5x5');
    const plantsData = {};
    const order = [];
    const plantBaseByDisplay = {};
    gridItems.forEach((item) => {
        const plantLabel = item.querySelector('.plant-label');
        if (!plantLabel) return;
        const displayName = (plantLabel.textContent || '').trim();
        if (!displayName) return;
        const baseName = (plantLabel.dataset.basePlant || displayName || '').trim() || displayName;
        if (!plantsData[displayName]) {
            plantsData[displayName] = 0;
            order.push(displayName);
        }
        plantsData[displayName]++;
        if (baseName !== displayName) {
            plantBaseByDisplay[displayName] = baseName;
        }
    });
    return { plants: order, plantCounts: plantsData, plantBaseByDisplay };
}

function loadImageFromBlob(blob) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image for compression'));
        };
        img.src = url;
    });
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create compressed image blob'));
        }, type, quality);
    });
}

async function compressBlobForJournalUpload(blob, targetBytes = JOURNAL_PLANT_INFO_TARGET_BYTES) {
    if (!blob || blob.size <= targetBytes) return blob;

    const img = await loadImageFromBlob(blob);
    const maxSide = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
    let scale = maxSide > JOURNAL_PLANT_INFO_MAX_DIMENSION
        ? JOURNAL_PLANT_INFO_MAX_DIMENSION / maxSide
        : 1;
    let bestBlob = blob;

    while (scale >= 0.4) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
        canvas.height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to compress image');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        let low = JOURNAL_PLANT_INFO_MIN_QUALITY;
        let high = 0.92;
        let candidate = await canvasToBlob(canvas, 'image/webp', high);

        if (candidate.size < bestBlob.size) bestBlob = candidate;

        for (let i = 0; i < 6; i++) {
            const quality = (low + high) / 2;
            const testBlob = await canvasToBlob(canvas, 'image/webp', quality);

            if (testBlob.size <= targetBytes) {
                candidate = testBlob;
                low = quality;
            } else {
                if (testBlob.size < bestBlob.size) bestBlob = testBlob;
                high = quality;
            }
        }

        if (candidate.size <= targetBytes) return candidate;
        if (candidate.size < bestBlob.size) bestBlob = candidate;
        scale *= JOURNAL_PLANT_INFO_SCALE_STEP;
    }

    return bestBlob;
}

/** Bed names from saved garden layouts (for Garden Plan Overview save to journal only). */
function getGardenPlanBedNames() {
    try {
        const raw = localStorage.getItem('garden_image_layouts');
        if (!raw) return [];
        const layouts = JSON.parse(raw);
        return (layouts || [])
            .map(layout => layout && layout.name && String(layout.name).trim())
            .filter(Boolean);
    } catch {
        return [];
    }
}

function showMessage(msg, isError = false) {
    const el = document.createElement('div');
    el.className = 'save-to-journal-toast' + (isError ? ' error' : '');
    el.textContent = msg;
    // Show toast a bit further down from the top, aligned towards the left
    el.style.cssText = 'position:fixed;top:330px;left:400px;padding:12px 20px;border-radius:8px;background:#475569;color:#f8fafc;z-index:10000;font-size:14px;box-shadow:0 4px 12px rgba(0,0,0,0.2);';
    if (isError) el.style.background = '#e63030';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 4000);
}

async function handleSaveToJournalClick() {
    try {
        await loadExportLibraries();
    } catch (e) {
        showMessage('Save to Journal is temporarily unavailable. Please try again.', true);
        return;
    }
    const gridEl = document.getElementById('grid');
    if (!gridEl) {
        showMessage('Garden grid not found. Please refresh the page.', true);
        return;
    }

    const nameInput = document.getElementById('garden-name');
    const name = nameInput ? nameInput.value.trim() : '';
    if (!name) {
        showMessage('Please name your garden bed before saving to journal.', true);
        nameInput?.focus();
        return;
    }

    const btn = document.getElementById('save-to-journal-bed');
    if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving…';
    }
    const yearSelect = document.getElementById('plan-for-year');
    const year = yearSelect ? parseInt(yearSelect.value, 10) : new Date().getFullYear();

    try {
        await assertGardenPlanLimitNotReached();
        await assertGardenPlanNameIsUnique(name);
        const { plants, plantCounts, plantBaseByDisplay } = getPlantSummaryFromGrid();
        const blob = await captureGardenGridAsBlob();
        const plantInfoBlob = await capturePlantSummaryAsBlob();
        const compressedPlantInfoBlob = plantInfoBlob
            ? await compressBlobForJournalUpload(plantInfoBlob)
            : null;
        await updateGardenCalendar();
        const calendarBlob = await captureGardenCalendarAsBlob();
        await savePlanSnapshotToJournal({
            name,
            plants,
            plantCounts,
            plantBaseByDisplay,
            imageBlob: blob,
            plantInfoBlob: compressedPlantInfoBlob || undefined,
            calendarBlob: calendarBlob || undefined,
            year
        });
        showMessage('Saved to your Dashboard journal!');
    } catch (err) {
        console.error('Save to journal failed:', err);
        const msg = err?.message || 'Failed to save to journal. Try again.';
        showMessage(msg, true);
    } finally {
        if (btn) {
            btn.disabled = false;
            btn.innerHTML = '<i class="fa fa-book"></i> Save to Journal';
        }
    }
}

function setupSaveToJournalButton() {
    const btn = document.getElementById('save-to-journal-bed');
    if (!btn) return;

    function updateButtonState() {
        const info = subscriptionManager.getSubscriptionInfo?.() ?? {};
        const { isLoggedIn = false, isPremium = false } = info;
        const allowed = isLoggedIn && isPremium;
        // Don't disable the button: disabled buttons don't fire click, so user gets no feedback.
        // Use a class for styling; click handler always runs and shows the right message.
        btn.classList.toggle('save-to-journal-unavailable', !allowed);
        btn.setAttribute('aria-disabled', allowed ? 'false' : 'true');
    }

    btn.addEventListener('click', () => {
        const info = subscriptionManager.getSubscriptionInfo?.() ?? {};
        const { isLoggedIn = false, isPremium = false } = info;
        if (!isLoggedIn) {
            const authModal = document.getElementById('show-auth-modal');
            if (authModal) authModal.click();
            showMessage('Please sign in to save to journal.', true);
            return;
        }
        if (!isPremium) return;
        handleSaveToJournalClick();
    });

    subscriptionManager.onSubscriptionChange?.(updateButtonState);
    updateButtonState();
}

/**
 * Save the current garden bed to the journal (same as "Save to Journal" button).
 * Performs auth/premium checks; shows message and returns without saving if not allowed.
 * Call this after capturing a garden layout to also send the bed to the journal.
 */
export function saveCurrentBedToJournal() {
    const info = subscriptionManager.getSubscriptionInfo?.() ?? {};
    const { isLoggedIn = false, isPremium = false } = info;
    if (!isLoggedIn) {
        const authModal = document.getElementById('show-auth-modal');
        if (authModal) authModal.click();
        showMessage('Please sign in to save to journal.', true);
        return;
    }
    if (!isPremium) return;
    handleSaveToJournalClick();
}

async function handleSaveGardenPlanToJournalClick() {
    try {
        await loadExportLibraries();
    } catch (e) {
        showMessage('Save to Journal is temporarily unavailable. Please try again.', true);
        return;
    }
    const container = document.getElementById('garden-beds-container');
    if (!container) {
        showMessage('Garden plan not found. Please refresh the page.', true);
        return;
    }

    // Fixed title for this save; list bed names (not plants)
    const name = 'Garden Plan Overview';
    const bedNames = getGardenPlanBedNames();

    const btn = document.getElementById('save-garden-plan-to-journal');
    if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving…';
    }
    const yearSelect = document.getElementById('plan-for-year');
    const year = yearSelect ? parseInt(yearSelect.value, 10) : new Date().getFullYear();

    try {
        await assertGardenPlanLimitNotReached();
        const blob = await captureGardenPlanAsBlob();
        // No plant summary image; pass bed names as the list (dashboard shows as "plants")
        await savePlanSnapshotToJournal({ name, plants: bedNames, imageBlob: blob, plantInfoBlob: undefined, year });
        showMessage('Saved to your Dashboard journal!');
    } catch (err) {
        console.error('Save to journal failed:', err);
        const msg = err?.message || 'Failed to save to journal. Try again.';
        showMessage(msg, true);
    } finally {
        if (btn) {
            btn.disabled = false;
            btn.innerHTML = '<i class="fa fa-book"></i> Save to Journal';
        }
    }
}

function setupSaveGardenPlanToJournalButton() {
    const btn = document.getElementById('save-garden-plan-to-journal');
    if (!btn) return;

    function updateButtonState() {
        const info = subscriptionManager.getSubscriptionInfo?.() ?? {};
        const { isLoggedIn = false, isPremium = false } = info;
        const allowed = isLoggedIn && isPremium;
        btn.classList.toggle('save-to-journal-unavailable', !allowed);
        btn.setAttribute('aria-disabled', allowed ? 'false' : 'true');
    }

    btn.addEventListener('click', () => {
        const info = subscriptionManager.getSubscriptionInfo?.() ?? {};
        const { isLoggedIn = false, isPremium = false } = info;
        if (!isLoggedIn) {
            const authModal = document.getElementById('show-auth-modal');
            if (authModal) authModal.click();
            showMessage('Please sign in to save to journal.', true);
            return;
        }
        if (!isPremium) return;
        handleSaveGardenPlanToJournalClick();
    });

    subscriptionManager.onSubscriptionChange?.(updateButtonState);
    updateButtonState();
}

/** Year range for "Plan for year" dropdown: current - 1 through current + 5 */
function getPlanYearRange() {
    const y = new Date().getFullYear();
    return { min: y - 1, max: y + 5 };
}

function setupPlanForYearSelect() {
    const select = document.getElementById('plan-for-year');
    if (!select) return;
    const { min, max } = getPlanYearRange();
    const currentYear = new Date().getFullYear();
    select.innerHTML = '';
    for (let year = min; year <= max; year++) {
        const opt = document.createElement('option');
        opt.value = String(year);
        opt.textContent = String(year);
        if (year === currentYear) opt.selected = true;
        select.appendChild(opt);
    }
}

export function initializeSaveToJournal() {
    setupPlanForYearSelect();
    setupSaveToJournalButton();
    setupSaveGardenPlanToJournalButton();
}
